<template>
  <v-card>
    <v-card-title class="pa-0">
      <v-app-bar color="secondary" dense>
        <v-toolbar-title>{{ t('imagePicker.title') }}</v-toolbar-title>
        <v-spacer />
        <v-select
          v-model="selectedCategoryIds"
          data-cy="image-picker-select"
          :items="categorySelectItems"
          hide-details
          prepend-icon="mdi-search"
          :placeholder="t('imagePicker.filterCategories')"
          prepend-inner-icon="mdi-folder-search-outline"
          item-value="id"
          item-title="text"
          multiple
          dense
          style="max-width: 300px"
        />
        <v-btn
          icon
          :aria-label="t('aria.label.closeImagePicker')"
          data-cy="image-picker-close-btn"
          @click="$emit('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-app-bar>
    </v-card-title>
    <v-card-text style="max-height: 800px; min-height: 400px; margin-top: 75px">
      <v-container>
        <v-row v-for="(category, categoryIndex) in selectedCategories" :key="categoryIndex">
          <v-list-item-title> {{ t(category.nameTranslationKey) }}</v-list-item-title>
          <v-col
            v-for="(image, imageIndex) in imagesInCategory(category.id)"
            :key="imageIndex"
            :ref="imageRefName(image.id)"
            sm="6"
            xs="12"
            md="4"
            cols="12"
          >
            <v-hover v-slot="{ isHovering }">
              <v-card
                :id="'donationImage' + image.id"
                v-ripple
                :class="{
                  'image-selected': image.id === imageId,
                  'pa-1': true,
                }"
                tabindex="0"
                data-cy="image-picker-card"
                tile
                class="pa-1"
                @click="imageId = image.id"
                @keydown.enter.space.prevent="imageId = image.id"
              >
                <v-img
                  data-cy="image-picker-image"
                  :src="image.url"
                  width="100%"
                  class="grey lighten-2"
                  aspect-ratio="1.5"
                />
                <v-fade-transition>
                  <div v-if="isHovering" key="hover-overlay">
                    <v-overlay color="accent" absolute />
                  </div>
                </v-fade-transition>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { DonationImage } from '@/interfaces/donation/donationImage';
import { DonationImageCategory } from '@/interfaces/donation/donationImageCategory';
import { useLabel } from '@/composables/useLabel';
import matomo from '@/services/matomoService';

export default defineComponent({
  props: {
    modelValue: {
      type: Number,
      default: null,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    images: {
      type: Array as () => DonationImage[],
      default: null,
    },
    categories: {
      type: Array as () => DonationImageCategory[],
      required: true,
    },
  },
  emits: ['close', 'update:modelValue'],
  setup(props, { emit }) {
    const categoryId = ref(null);
    const searchMode = ref(false);
    const imageId = ref(props.modelValue);
    const selectedCategoryIds = ref<number[]>([]);
    const query = ref(null);

    const categoryName = computed(() => {
      if (categoryId.value) {
        const cat = props.categories.find((c) => c.id == categoryId.value);
        return cat ? cat.name : null;
      }
      return null;
    });

    const selectedCategories = computed(() => {
      if (selectedCategoryIds.value.length) {
        return props.categories.filter((c) => selectedCategoryIds.value.includes(c.id));
      }
      return props.categories;
    });

    watch(imageId, (val) => {
      emit('close');
      emit('update:modelValue', val);
      matomo.trackEvent('donations_certificate_image_selected_custom', 'certificate', 'donations_certificate_image_selected_custom');
    });

    const imageRefName = (id) => 'image-' + id;

    const isSelectedCategory = (id: number) => {
      return selectedCategoryIds.value.includes(id);
    };

    const toggleCategory = (id: number) => {
      if (isSelectedCategory(id)) {
        selectedCategoryIds.value = selectedCategoryIds.value.filter((i) => i != id);
      } else {
        selectedCategoryIds.value.push(id);
      }
    };

    const iconClick = () => {
      if (categoryId.value == null) {
        emit('close');
      } else {
        categoryId.value = null;
      }
    };

    const imagesInCategory = (categoryId: number) => {
      if (!props.images) return [];
      return props.images.filter((i) => i.donationImageCategory.id == categoryId);
    };

    const { t } = useLabel();

    const categorySelectItems = computed(() => {
      return props.categories.map((c) => {
        const translated = t(c.nameTranslationKey);
        const text =
          (translated && translated.trim()) ||
          (typeof c.name === 'string' && c.name) ||
          c.nameTranslationKey ||
          String(c.id);
        return { id: c.id, text };
      });
    });

    return {
      t,
      categoryId,
      searchMode,
      imageId,
      selectedCategoryIds,
      query,
      categoryName,
      selectedCategories,
      imageRefName,
      isSelectedCategory,
      toggleCategory,
      iconClick,
      imagesInCategory,
      categorySelectItems,
    };
  },
});
</script>
<style scoped lang="scss">
.v-card[tabindex='0']:focus-visible {
  outline: 2px solid var(--primary-color-highlight);
  box-shadow: 0 0 0 2px var(--primary-color-highlight);
  outline-offset: 2px;
}
</style>
